import type { ComponentType, ReactNode } from 'react';
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  Building2,
  Calendar,
  ClipboardCheck,
  ClipboardList,
  FileText,
  FolderKanban,
  HelpCircle,
  Home,
  Inbox,
  LayoutDashboard,
  Link2,
  Receipt,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
  Wrench,
  Lightbulb,
} from 'lucide-react';
import type { PermissionKey } from '@mesterplan/shared-types';

export interface NavigationItem {
  label: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  permission?: PermissionKey;
  permissionOr?: PermissionKey;
  badge?: ReactNode;
  externalHref?: string;
  /** Not a route: the sidebar renders a button that runs this action instead of a link. */
  action?: 'leaveSupplierSession';
}

export interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

interface NavigationContext {
  isPartnerUser: boolean;
  isPartnerCompany: boolean;
  calculationEnabled: boolean;
  /** An account manager inside a customer: the capped menu applies. */
  isSupplierSession?: boolean;
  /** Name of the supplier org, for "Tilbage til …". */
  supplierName?: string;
  hasPermission: (permission: PermissionKey) => boolean;
}

const mainNavItems: NavigationItem[] = [
  { label: 'Dashboard', path: '/', icon: LayoutDashboard, permission: 'dashboard.view' },
  { label: 'Brugere', path: '/users', icon: Users, permission: 'users.view' },
  { label: 'Projekter', path: '/projects', icon: FolderKanban, permission: 'projects.view' },
  { label: 'Kunder', path: '/customers', icon: Building2, permission: 'customers.view' },
  { label: 'Ejendomme', path: '/properties', icon: Home, permission: 'properties.view' },
  { label: 'Anlæg', path: '/assets', icon: Wrench, permission: 'assets.view' },
];

const baseBusinessNavItems: NavigationItem[] = [
  { label: 'Tilbud', path: '/quotes', icon: FileText, permission: 'quotes.view' },
  { label: 'Prisbibliotek', path: '/price-library', icon: BookOpen, permission: 'priceLibrary.view' },
  { label: 'AI-analyse', path: '/ai-analyse', icon: Sparkles, permission: 'calculations.edit' },
  { label: 'Fakturaer', path: '/invoices', icon: Receipt, permission: 'invoices.view' },
  { label: 'E-faktura', path: '/efaktura', icon: Inbox, permission: 'invoices.view' },
  { label: 'Budgettering', path: '/budgettering', icon: Wallet, permission: 'budgettering.view', permissionOr: 'budgettering.viewTimeOverview' },
  { label: 'Planlægning', path: '/planning', icon: Calendar, permission: 'planning.view' },
  { label: 'Rapporter', path: '/reports', icon: BarChart3, permission: 'reports.view' },
  { label: 'Indsigter', path: '/insights', icon: Lightbulb, permission: 'insights.view' },
  { label: 'Logs', path: '/logs', icon: ClipboardList, permission: 'reports.view' },
];

const qualityNavItems: NavigationItem[] = [
  { label: 'KS', path: '/ks', icon: ClipboardCheck, permission: 'quality.view' },
  { label: 'KLS', path: '/kls', icon: ShieldCheck, permission: 'quality.view' },
];

const systemNavItems: NavigationItem[] = [
  { label: 'Indstillinger', path: '/settings', icon: Settings, permission: 'settings.view' },
  { label: 'Connect', path: '/marketplace', icon: Link2, permission: 'settings.view' },
  { label: 'Hjælp', path: '/help', icon: HelpCircle },
  { label: 'Privatlivspolitik', path: '/privacy-policy', icon: Shield },
  { label: 'GDPR', path: 'https://mesterplan.dk/pages/gdpr.html', icon: ShieldCheck, externalHref: 'https://mesterplan.dk/pages/gdpr.html' },
];

// The ceiling for an account manager inside a customer. Each item is still
// permission-filtered, so the session never shows more than the grant allows.
// "Materialer" has no page of its own (it is a tab on the project), so it is not listed.
const supplierSessionItems: NavigationItem[] = [
  { label: 'Projekter', path: '/projects', icon: FolderKanban, permission: 'projects.view' },
  { label: 'Tilbud', path: '/quotes', icon: FileText, permission: 'quotes.view' },
  { label: 'AI-analyse', path: '/ai-analyse', icon: Sparkles, permission: 'calculations.edit' },
  { label: 'Prisbibliotek', path: '/price-library', icon: BookOpen, permission: 'priceLibrary.view' },
  // The e-faktura inbox, narrowed server-side to invoices from the AM's own supplier and read-only here.
  // Opt-in: the customer must have given the supplier creditorInvoices.view.
  { label: 'Bilag', path: '/efaktura', icon: Inbox, permission: 'creditorInvoices.view' },
];

const hasAccess = (item: NavigationItem, hasPermission: NavigationContext['hasPermission']): boolean =>
  !item.permission || hasPermission(item.permission) || Boolean(item.permissionOr && hasPermission(item.permissionOr));

function visibleSection(title: string, items: NavigationItem[], hasPermission: NavigationContext['hasPermission']): NavigationSection | null {
  const visibleItems = items.filter((item) => hasAccess(item, hasPermission));
  return visibleItems.length > 0 ? { title, items: visibleItems } : null;
}

export function getNavigationSections(context: NavigationContext): NavigationSection[] {
  const { isPartnerUser, isPartnerCompany, calculationEnabled, isSupplierSession, supplierName, hasPermission } = context;

  if (isSupplierSession) {
    const customerItems = supplierSessionItems.filter(
      (item) => (calculationEnabled || item.path !== '/ai-analyse') && hasAccess(item, hasPermission),
    );
    const systemItems: NavigationItem[] = [
      { label: supplierName ? `Tilbage til ${supplierName}` : 'Tilbage', path: '/', icon: ArrowLeft, action: 'leaveSupplierSession' },
      { label: 'Hjælp', path: '/help', icon: HelpCircle },
    ];
    return [
      ...(customerItems.length > 0 ? [{ title: 'Kunde', items: customerItems }] : []),
      { title: 'System', items: systemItems },
    ];
  }

  if (isPartnerUser || isPartnerCompany) {
    const overviewItems: NavigationItem[] = [
      { label: 'Dashboard', path: '/', icon: LayoutDashboard },
      { label: 'Projekter', path: '/projects', icon: FolderKanban },
      { label: 'Tilbud', path: '/quotes', icon: FileText },
      ...(isPartnerCompany ? [{ label: 'Brugere', path: '/users', icon: Users }] : []),
    ];
    const supportItems: NavigationItem[] = [
      ...(isPartnerCompany ? [{ label: 'Indstillinger', path: '/settings', icon: Settings }] : []),
      { label: 'Hjælp', path: '/help', icon: HelpCircle },
      { label: 'Privatlivspolitik', path: '/privacy-policy', icon: Shield },
      { label: 'GDPR', path: 'https://mesterplan.dk/pages/gdpr.html', icon: ShieldCheck, externalHref: 'https://mesterplan.dk/pages/gdpr.html' },
    ];
    return [
      { title: 'Oversigt', items: overviewItems },
      { title: 'System', items: supportItems },
    ];
  }

  const businessItems = calculationEnabled
    ? baseBusinessNavItems
    : baseBusinessNavItems.filter((item) => item.path !== '/ai-analyse');
  const sections = [
    visibleSection('Oversigt', mainNavItems, hasPermission),
    visibleSection('Forretning', businessItems, hasPermission),
    visibleSection('Kvalitet', qualityNavItems, hasPermission),
    visibleSection('System', systemNavItems, hasPermission),
  ];
  return sections.filter((section): section is NavigationSection => section !== null);
}
